import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import {useVirtualizer} from "@tanstack/react-virtual";
import {useEffect, useId, useRef, useState} from "react";
import {VIRTUAL_PAGE_ITEM_HEIGHT, VIRTUAL_PAGE_OVERSCAN, VIRTUAL_PAGE_VISIBLE_ITEM_COUNT} from "./constants";
import type {VirtualPagePickerProps} from "./types";
import {getVirtualPageModel} from "./utils";

const VirtualPagePicker = ({
    anchorEl,
    currentPage,
    pageCount,
    onChange,
    onClose
}: VirtualPagePickerProps) => {
    const listboxId = useId();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [value, setValue] = useState('');
    const [activePage, setActivePage] = useState<number | null>(null);
    const {
        requestedPage,
        requestedPageIsValid,
        itemCount: pageItemCount,
        pageAtIndex
    } = getVirtualPageModel(value, pageCount);
    const listHeight = Math.min(pageItemCount, VIRTUAL_PAGE_VISIBLE_ITEM_COUNT) * VIRTUAL_PAGE_ITEM_HEIGHT;
    const virtualizer = useVirtualizer({
        count: pageItemCount,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => VIRTUAL_PAGE_ITEM_HEIGHT,
        getItemKey: index => pageAtIndex(index),
        overscan: VIRTUAL_PAGE_OVERSCAN,
        initialRect: {width: 68, height: listHeight || VIRTUAL_PAGE_ITEM_HEIGHT}
    });

    useEffect(() => {
        if (!anchorEl) return;

        setValue('');
        setActivePage(null);
    }, [anchorEl, currentPage]);

    useEffect(() => {
        if (!anchorEl) return;

        const targetPage = activePage ?? (value && requestedPageIsValid ? requestedPage : currentPage);
        const targetIndex = value ? targetPage === requestedPage ? 0 : -1 : targetPage - 1;
        const frame = window.requestAnimationFrame(() => {
            virtualizer.measure();
            if (targetIndex >= 0) {
                virtualizer.scrollToIndex(targetIndex, {
                    align: activePage === null && !value ? 'center' : 'auto'
                });
            } else {
                scrollRef.current?.scrollTo({top: 0});
            }
        });

        return () => window.cancelAnimationFrame(frame);
    }, [activePage, anchorEl, currentPage, requestedPage, requestedPageIsValid, value, virtualizer]);

    const moveActivePage = (offset: number) => {
        if (!pageItemCount) return;

        if (activePage === null) {
            if (value) {
                setActivePage(requestedPage);
                return;
            }

            const firstVisibleIndex = Math.min(
                Math.floor((scrollRef.current?.scrollTop ?? 0) / VIRTUAL_PAGE_ITEM_HEIGHT),
                pageItemCount - 1
            );
            const lastVisibleIndex = Math.min(
                firstVisibleIndex + VIRTUAL_PAGE_VISIBLE_ITEM_COUNT - 1,
                pageItemCount - 1
            );
            setActivePage(pageAtIndex(offset > 0 ? firstVisibleIndex : lastVisibleIndex));
            return;
        }

        const activeIndex = value ? 0 : activePage - 1;
        const nextIndex = Math.min(Math.max(activeIndex + offset, 0), pageItemCount - 1);
        setActivePage(pageAtIndex(nextIndex));
    };

    const submitPage = (page: number) => {
        if (page < 1 || page > pageCount) return;

        onChange(page);
        onClose();
    };

    return (
        <Popper
            open={!!anchorEl}
            anchorEl={anchorEl}
            placement="bottom"
            modifiers={[
                {name: 'offset', options: {offset: [0, 6]}},
                {name: 'flip', options: {fallbackPlacements: ['top']}},
                {name: 'preventOverflow', options: {padding: 8}}
            ]}
            sx={{zIndex: theme => theme.zIndex.modal}}
        >
            <ClickAwayListener onClickAway={onClose}>
                <Paper
                    elevation={6}
                    sx={{
                        width: 84,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2
                    }}
                >
                    <Box sx={{p: 1}}>
                        <InputBase
                            autoFocus
                            value={value}
                            placeholder={`1–${pageCount.toLocaleString()}`}
                            inputProps={{
                                'aria-label': `Page number, 1 to ${pageCount}`,
                                'aria-activedescendant': activePage === null
                                    ? undefined
                                    : `${listboxId}-option-${activePage}`,
                                'aria-autocomplete': 'list',
                                'aria-controls': pageItemCount ? listboxId : undefined,
                                'aria-expanded': pageItemCount > 0,
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                role: 'combobox'
                            }}
                            onChange={event => {
                                const nextValue = event.target.value.replace(/\D/g, '');
                                setValue(nextValue);
                                setActivePage(null);
                            }}
                            onKeyDown={event => {
                                if (event.key === 'Enter' && activePage !== null) {
                                    event.preventDefault();
                                    submitPage(activePage);
                                } else if (event.key === 'Enter' && requestedPageIsValid) {
                                    event.preventDefault();
                                    submitPage(requestedPage);
                                } else if (event.key === 'Escape') {
                                    event.preventDefault();
                                    onClose();
                                } else if (event.key === 'ArrowDown') {
                                    event.preventDefault();
                                    moveActivePage(1);
                                } else if (event.key === 'ArrowUp') {
                                    event.preventDefault();
                                    moveActivePage(-1);
                                } else if (event.key === 'PageDown') {
                                    event.preventDefault();
                                    moveActivePage(VIRTUAL_PAGE_VISIBLE_ITEM_COUNT);
                                } else if (event.key === 'PageUp') {
                                    event.preventDefault();
                                    moveActivePage(-VIRTUAL_PAGE_VISIBLE_ITEM_COUNT);
                                }
                            }}
                            sx={{
                                width: '100%',
                                height: 32,
                                border: '1px solid',
                                borderColor: value && !requestedPageIsValid ? 'error.main' : 'divider',
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                '&:focus-within': {
                                    borderColor: value && !requestedPageIsValid ? 'error.main' : 'primary.main'
                                },
                                '& input': {px: 1, py: 0, textAlign: 'left'}
                            }}
                        />
                    </Box>

                    {pageItemCount > 0 && (
                        <Box
                            id={listboxId}
                            ref={scrollRef}
                            role="listbox"
                            aria-label="Pages"
                            sx={{
                                position: 'relative',
                                height: listHeight,
                                overflowY: 'auto',
                                overscrollBehavior: 'contain',
                                borderTop: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Box sx={{height: virtualizer.getTotalSize(), position: 'relative'}}>
                                {virtualizer.getVirtualItems().map(virtualItem => {
                                    const page = pageAtIndex(virtualItem.index);
                                    const selected = page === currentPage;
                                    const active = page === activePage;

                                    return (
                                        <Button
                                            key={virtualItem.key}
                                            id={`${listboxId}-option-${page}`}
                                            type="button"
                                            role="option"
                                            aria-selected={selected}
                                            color="inherit"
                                            variant="text"
                                            onMouseEnter={() => setActivePage(page)}
                                            onClick={() => submitPage(page)}
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: VIRTUAL_PAGE_ITEM_HEIGHT,
                                                transform: `translateY(${virtualItem.start}px)`,
                                                justifyContent: 'space-between',
                                                px: 1.5,
                                                borderRadius: 0,
                                                bgcolor: active ? 'action.hover' : 'transparent',
                                                fontWeight: selected ? 500 : 400,
                                                textTransform: 'none'
                                            }}
                                        >
                                            {page.toLocaleString()}
                                            {selected && <CheckIcon color="primary" sx={{fontSize: 16}}/>}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
};

export default VirtualPagePicker;
